import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { AgentWizardData } from '@/schemas/agent-wizard'
import { useOrganizationStore } from '@/stores/organization-store'
import {
  toolRegistry,
  builtinToolProvider,
  BuiltinToolProvider,
  type Tool,
} from '@/lib/tools'
import { GoogleDriveFolderPicker } from '@/components/integrations/google-drive-folder-picker'
import { NotionPagePicker } from '@/components/integrations/notion-page-picker'
import { Search, Zap, Check, AlertCircle, Settings, Folder, FileText } from 'lucide-react'
import { useDebounce } from '@/hooks/use-debounce'

// Tools that support configuration
const CONFIGURABLE_TOOLS = ['google_drive', 'Google Drive', 'notion', 'Notion']

// Check if tool is Google Drive
const isGoogleDriveTool = (tool: Tool) =>
  tool.name === 'Google Drive' || tool.id === 'google_drive'

// Check if tool is Notion
const isNotionTool = (tool: Tool) =>
  tool.name === 'Notion' || tool.id === 'notion' ||
  (tool.provider === 'builtin' && (tool as unknown as { config?: { internal_name?: string } }).config?.internal_name === 'notion')

export function StepTools() {
  const { t } = useTranslation('agents')
  const { watch, setValue } = useFormContext<AgentWizardData>()
  const currentOrganization = useOrganizationStore(
    (state) => state.currentOrganization
  )

  const [tools, setTools] = useState<Tool[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  const [notionPickerOpen, setNotionPickerOpen] = useState(false)
  const [configuringToolId, setConfiguringToolId] = useState<string | null>(null)

  const debouncedSearch = useDebounce(searchQuery, 300)
  // Ensure selectedToolIds is always an array (handles undefined from form state)
  const watchedToolIds = watch('tool_ids')
  const selectedToolIds = Array.isArray(watchedToolIds) ? watchedToolIds : []

  // Get tool configs
  const watchedToolConfigs = watch('tool_configs')
  const toolConfigs = watchedToolConfigs || {}

  // Load tools on mount
  useEffect(() => {
    async function loadTools() {
      setIsLoading(true)
      setError(null)

      try {
        // Initialize provider with organization context
        if (currentOrganization?.id) {
          builtinToolProvider.setOrganizationId(currentOrganization.id)
        }

        // Register provider if not already registered
        if (!toolRegistry.hasProvider('builtin')) {
          toolRegistry.registerProvider(builtinToolProvider)
        }

        // Try to load real tools from API
        await toolRegistry.initializeAll()
        let loadedTools = await toolRegistry.getAllTools({ enabledOnly: true })

        // If no tools from API, use demo tools for development
        if (loadedTools.length === 0) {
          loadedTools = BuiltinToolProvider.getDemoTools()
        }

        setTools(loadedTools)
      } catch {
        // Fallback to demo tools on error
        setTools(BuiltinToolProvider.getDemoTools())
        setError(t('wizard.tools.serverError'))
      } finally {
        setIsLoading(false)
      }
    }

    loadTools()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id])

  // Filter tools based on search
  const filteredTools = debouncedSearch
    ? tools.filter(
        (t) =>
          t.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          t.description.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          t.category.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : tools

  // Group tools by category
  const toolsByCategory = filteredTools.reduce(
    (acc, tool) => {
      if (!acc[tool.category]) {
        acc[tool.category] = []
      }
      acc[tool.category].push(tool)
      return acc
    },
    {} as Record<string, Tool[]>
  )

  const toggleTool = (toolId: string) => {
    const newSelection = selectedToolIds.includes(toolId)
      ? selectedToolIds.filter((id) => id !== toolId)
      : [...selectedToolIds, toolId]
    setValue('tool_ids', newSelection)

    // If deselecting, remove config
    if (!newSelection.includes(toolId)) {
      const newConfigs = { ...toolConfigs }
      delete newConfigs[toolId]
      setValue('tool_configs', newConfigs)
    }
  }

  const isSelected = (toolId: string) => selectedToolIds.includes(toolId)

  const isConfigurable = (tool: Tool) => {
    return CONFIGURABLE_TOOLS.includes(tool.name) || CONFIGURABLE_TOOLS.includes(tool.id)
  }

  const getToolConfig = (toolId: string) => {
    return toolConfigs[toolId] || {}
  }

  const handleConfigureTool = (e: React.MouseEvent, tool: Tool) => {
    e.stopPropagation()
    setConfiguringToolId(tool.id)

    if (isGoogleDriveTool(tool)) {
      setFolderPickerOpen(true)
    } else if (isNotionTool(tool)) {
      setNotionPickerOpen(true)
    }
  }

  const handleFolderSelect = (folder: { id: string; name: string; path: string }) => {
    if (configuringToolId) {
      const newConfigs = {
        ...toolConfigs,
        [configuringToolId]: {
          ...getToolConfig(configuringToolId),
          folder_id: folder.id,
          folder_name: folder.name,
          folder_path: folder.path,
        },
      }
      setValue('tool_configs', newConfigs)
    }
    setFolderPickerOpen(false)
    setConfiguringToolId(null)
  }

  const handleNotionPageSelect = (pages: { id: string; title: string }[]) => {
    if (configuringToolId && pages.length > 0) {
      const page = pages[0] // Use first page as parent
      const newConfigs = {
        ...toolConfigs,
        [configuringToolId]: {
          ...getToolConfig(configuringToolId),
          parent_id: page.id,
          parent_title: page.title,
        },
      }
      setValue('tool_configs', newConfigs)
    }
    setNotionPickerOpen(false)
    setConfiguringToolId(null)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-gray-400">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-600 border-t-indigo-500" />
          <span>{t('wizard.tools.loading')}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="p-4 rounded-xl bg-white/5 border border-gray-700/50 animate-pulse"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gray-700/50" />
                <div className="flex-1">
                  <div className="h-4 w-24 bg-gray-700/50 rounded mb-2" />
                  <div className="h-3 w-40 bg-gray-700/30 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Label className="text-gray-300 flex items-center gap-2">
          <Zap className="h-4 w-4" />
          {t('wizard.tools.available')}
        </Label>
        <span className="text-sm text-gray-500">
          {t('wizard.tools.selected', { count: selectedToolIds.length })}
        </span>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          type="text"
          placeholder={t('wizard.tools.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white/5 border-gray-700/50 text-white placeholder:text-gray-500"
        />
      </div>

      {/* Tools Grid */}
      {filteredTools.length === 0 ? (
        <div className="text-center py-12">
          <Zap className="h-12 w-12 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">
            {searchQuery
              ? t('wizard.tools.noToolsFound')
              : t('wizard.tools.noToolsAvailable')}
          </p>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="mt-2 text-sm text-indigo-400 hover:text-indigo-300"
            >
              {t('wizard.tools.clearSearch')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(toolsByCategory).map(([category, categoryTools]) => (
            <div key={category}>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                {category}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {categoryTools.map((tool) => {
                  const selected = isSelected(tool.id)
                  const configurable = isConfigurable(tool)
                  const config = getToolConfig(tool.id)
                  const hasConfig = Object.keys(config).length > 0

                  return (
                    <div
                      key={tool.id}
                      className={cn(
                        'p-4 rounded-xl transition-all duration-200',
                        'bg-white/5 border',
                        selected
                          ? 'border-indigo-500 bg-indigo-500/10'
                          : 'border-gray-700/50 hover:border-gray-600 hover:bg-white/10'
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleTool(tool.id)}
                        className="w-full text-left"
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              'flex h-10 w-10 items-center justify-center rounded-lg text-xl',
                              selected
                                ? 'bg-indigo-500/30'
                                : 'bg-gray-700/50'
                            )}
                          >
                            {tool.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-white truncate">
                                {tool.name}
                              </p>
                              {selected && (
                                <Check className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-gray-400 line-clamp-2">
                              {tool.description}
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Configuration section for selected configurable tools */}
                      {selected && configurable && (
                        <div className="mt-3 pt-3 border-t border-gray-700/50">
                          {/* Google Drive config */}
                          {isGoogleDriveTool(tool) && (
                            hasConfig && config.folder_path ? (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-gray-400">
                                  <Folder className="h-4 w-4" />
                                  <span className="truncate max-w-[150px]">
                                    {config.folder_path as string}
                                  </span>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => handleConfigureTool(e, tool)}
                                  className="text-xs text-indigo-400 hover:text-indigo-300"
                                >
                                  Alterar
                                </Button>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleConfigureTool(e, tool)}
                                className="w-full justify-start text-gray-400 hover:text-white"
                              >
                                <Settings className="h-4 w-4 mr-2" />
                                Selecionar pasta do Drive
                              </Button>
                            )
                          )}

                          {/* Notion config */}
                          {isNotionTool(tool) && (
                            hasConfig && config.parent_title ? (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-gray-400">
                                  <FileText className="h-4 w-4" />
                                  <span className="truncate max-w-[150px]">
                                    {config.parent_title as string}
                                  </span>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => handleConfigureTool(e, tool)}
                                  className="text-xs text-indigo-400 hover:text-indigo-300"
                                >
                                  Alterar
                                </Button>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleConfigureTool(e, tool)}
                                className="w-full justify-start text-gray-400 hover:text-white"
                              >
                                <Settings className="h-4 w-4 mr-2" />
                                Selecionar pagina padrao
                              </Button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <p className="text-xs text-gray-500">
        {t('wizard.tools.toolsInfo')}
      </p>

      {/* Google Drive Folder Picker */}
      {currentOrganization?.id && (
        <GoogleDriveFolderPicker
          open={folderPickerOpen}
          onOpenChange={setFolderPickerOpen}
          organizationId={currentOrganization.id}
          onSelect={handleFolderSelect}
        />
      )}

      {/* Notion Page Picker */}
      {currentOrganization?.id && (
        <NotionPagePicker
          open={notionPickerOpen}
          onOpenChange={setNotionPickerOpen}
          organizationId={currentOrganization.id}
          selectedPages={
            configuringToolId && toolConfigs[configuringToolId]?.parent_id
              ? [{
                  id: toolConfigs[configuringToolId].parent_id as string,
                  title: toolConfigs[configuringToolId].parent_title as string || '',
                }]
              : []
          }
          onSelect={handleNotionPageSelect}
        />
      )}
    </div>
  )
}
